import type { EventBus } from "../core/EventBus.ts";

/**
 * The slice of `AudioEngine` the director drives. Narrowed to a tiny interface
 * so the state machine is unit-testable with a spy mock (jsdom has no Web Audio,
 * so real playback/fades are an owner ear-check — see PT-19 §8).
 *
 * BL-68 additions (optional — backward-compatible): setMusicBrightness,
 * setMusicRate, isMusicPlaying for the adaptive single-loop architecture.
 */
export interface MusicEngine {
  playMusicFadeIn: (track: string, fadeMs?: number) => void;
  fadeOutMusic: (fadeMs?: number) => void;
  resumeContext?: () => void;
  setMusicBrightness?: (brightness: number, fadeMs?: number) => void;
  setMusicRate?: (rate: number, fadeMs?: number) => void;
  isMusicPlaying?: () => boolean;
  // PT-37 additions (optional — backward-compatible, like the BL-68 set):
  // stereo width (M/S side gain) + the interlude pause-duck.
  setMusicWidth?: (width: number, fadeMs?: number) => void;
  setMusicDuck?: (db: number, fadeMs?: number) => void;
  // Task-002 additions (2026-ingame-music-rework, AC-4/AC-2 — optional,
  // backward-compatible, like the sets above): the composer's ONE energy
  // input, and a narrow diagnostics-trace hook so the director can record a
  // "music.handoff" crumb on the engine's existing ring buffer without the
  // engine exposing its private trace() choke point directly.
  setMusicEnergy?: (energy: number, fadeMs?: number) => void;
  traceMusicEvent?: (kind: string, detail?: string) => void;
  // Task-001 additions (2026-audio-polish, AC-3/AC-4/AC-5 — optional,
  // backward-compatible, like every set above): starting the theme with its
  // intro FX armed (`enterTitle()`), starting the deploy outro (`onDeploy()`),
  // and reading whether that outro is still in flight (`enterCombatSweep()`'s
  // crossfade-vs-bus-fade branch). A bare engine missing these degrades to
  // today's shipped shape exactly (plain `playMusicFadeIn` + the old bus-fade
  // hand-off) — no behavior change for a caller that never implements them.
  playThemeIntro?: (track: string, fadeMs?: number) => void;
  startThemeOutro?: () => void;
  isThemeOutroInFlight?: () => boolean;
  // Task-003 addition (2026-audio-polish, AC-10 — optional, backward-
  // compatible, like every set above): the interlude underwater muffle,
  // driven alongside the brightness/width dim by `setInterlude` below. A
  // bare engine missing this degrades to today's dim+duck-only interlude
  // exactly — no behavior change for a caller that never implements it.
  setMusicMuffle?: (active: boolean, fadeMs?: number) => void;
}

export type MusicScene =
  | "silent"
  | "boot"
  | "title"
  | "menu"
  | "deploy"
  | "combat"
  | "boss"
  | "victory"
  | "defeat";

interface ScenePreset {
  brightness: number;
  /** Stereo M/S width (PT-37): <1 narrow/intimate, 1 neutral, >1 wide/bloom. */
  width: number;
  rate: number;
  fadeMs: number;
  /** AC-4 (2026-ingame-music-rework) — the composer's energy target (0..1),
   *  folded through the SAME intensity/pressure algebra as brightness/rate
   *  (`effectiveSceneTone`) and written via `setMusicEnergy` (a safe no-op
   *  without a live composer). `combat`'s 0.45 matches
   *  `MusicPatterns.DEFAULT_ENERGY` — the composer's own pre-director
   *  baseline — by design (tech-analysis §2.4). */
  energy: number;
}

/**
 * Per-scene filter/width/rate/energy presets. Filter/width/rate drive the
 * CHAIN (brightness/width/rate nodes — chain-side, source-independent);
 * energy drives the RUN TRACK's composer layer mix specifically (a safe
 * no-op while the theme, which has no energy input, is the live source).
 *
 * PT-37 "bloom" (owner-signed, ear-checkable named constants): the in-run combat
 * scene lifts brightness 0.6 → 0.90 (≈ −2 dB shelf, essentially flat — open but
 * never *klirrt*) and the *width* (1.25) carries the "breit/wide" feel; boss
 * blooms wider still (1.40). Pre-game/deploy stay narrow + filtered (dumpf).
 */
const PRESETS: Record<MusicScene, ScenePreset> = {
  silent: { brightness: 0, width: 1.0, rate: 1.0, fadeMs: 0, energy: 0 },
  boot: { brightness: 0.1, width: 0.6, rate: 0.98, fadeMs: 2000, energy: 0.1 },
  // Task-001 (2026-audio-polish, AC-3, P-12) — starts near the menu preset
  // (brightness ≈0.3, width ≈0.7, energy 0.2): the intro FX (armed by
  // `enterTitle()`, engine-side) carry the drama, so the chain-side tone
  // itself stays calm. `fadeMs` matches `THEME_INTRO_LP_SWEEP_MS`
  // (AudioEngine.ts) by design — the bus volume fade-in and the LPF/phaser
  // intro sweep complete together, the "DJ-style" build the owner asked for.
  title: { brightness: 0.3, width: 0.7, rate: 1.0, fadeMs: 4000, energy: 0.2 },
  menu: { brightness: 0.35, width: 0.7, rate: 1.0, fadeMs: 1200, energy: 0.2 },
  deploy: { brightness: 0.2, width: 0.65, rate: 1.0, fadeMs: 800, energy: 0.15 },
  combat: { brightness: 0.9, width: 1.25, rate: 1.0, fadeMs: 600, energy: 0.45 },
  boss: { brightness: 1.0, width: 1.4, rate: 1.04, fadeMs: 400, energy: 0.9 },
  victory: { brightness: 0.8, width: 1.1, rate: 1.0, fadeMs: 800, energy: 0.5 },
  defeat: { brightness: 0.1, width: 0.5, rate: 0.96, fadeMs: 1200, energy: 0.1 },
};

/**
 * Interlude overlay (PT-37) — owner-signed, ear-checkable named constants. The
 * interlude is a *filter/width/duck overlay on the running loop*, NOT a scene:
 * dumpf (0.30 brightness) + narrow (0.65 width), optionally ducked (pause).
 * Asymmetric ramps (350 in / 450 out) make the bloom feel deliberate.
 */
const INTERLUDE_BRIGHTNESS = 0.3;
const INTERLUDE_WIDTH = 0.65;
const INTERLUDE_DUCK_DB = -4.5;
const INTERLUDE_IN_MS = 350;
const INTERLUDE_OUT_MS = 450;
/** S195/BL-283 — near-loss pressure ramp (slow enough to breathe, not lurch). */
const PRESSURE_RAMP_MS = 400;
/** Task-011 (2026-gameplay-reworks, AC-14/O-6, D-325) — the spawn-surge ramp.
 *  Faster than the pressure ramp: a breach burst should swell audibly INSIDE
 *  the burst itself, not lag behind it. */
const SPAWN_SURGE_RAMP_MS = 250;

/**
 * S143 / BL-129(b): the prescreen→combat "french-house" filter sweep.
 *
 * The owner wants a smooth, single-shot riser at the one entering-the-battle
 * beat (PT-39 #2): dull/filtered → bright/open as the run begins. We reuse the
 * existing brightness + width primitives (no new nodes); the sweep is just a
 * longer ramp from a deliberately *deeper* dampened start (below the deploy
 * preset) up to the bright combat tone. The deeper start IS the anticipation —
 * the loop has been dumpf during the deploy click; the sweep takes it *further*
 * down for a beat, then opens.
 *
 * SWEEP_DAMPEN_BRIGHTNESS — the cellar the sweep climbs out of. Lower than
 *   the deploy preset (0.2) so the lift has meaningful range; not zero
 *   (which would risk feeling dead during the brief settle).
 * SWEEP_DURATION_MS — long enough to read as a sweep, short enough to land
 *   when wave-1 starts (the deploy curtain is ~800ms; the sweep peaks
 *   slightly after the player sees the board come alive).
 */
const SWEEP_DAMPEN_BRIGHTNESS = 0.08;
const SWEEP_DAMPEN_WIDTH = 0.55;
const SWEEP_SETTLE_MS = 200;
const SWEEP_DURATION_MS = 1600;

/**
 * AC-2 (2026-ingame-music-rework, D-240 hand-off) — how long `fadeOutMusic`
 * takes to dissolve the theme down into the sweep cellar before `runTrack`
 * starts (tech-analysis §1.3's "≈450 ms"); also the delay before the sweep's
 * settle→lift timer fires in that case, so the two stay in lockstep — no
 * double-playback window (the theme is fully hard-stopped — cross-platform,
 * incl. the iOS native branch — no later than this many ms into the hand-off).
 */
const HANDOFF_FADE_MS = 450;
/** How long `runTrack`'s own bus gain takes to reach its held level once it
 *  starts inside the already-dampened cellar tone — quick, click-free onset
 *  only; the audible "opening" is carried entirely by the SWEEP_DURATION_MS
 *  brightness/width lift that fires alongside it, not a second slow fade. */
const HANDOFF_BLOOM_FADE_MS = 200;
/**
 * AC-5 (2026-audio-polish) — the CROSSFADE hand-off variant: when a
 * deploy-triggered theme outro is already in flight (`onDeploy()` started it;
 * `AudioEngine.isThemeOutroInFlight()`), `enterCombatSweep()` skips the old
 * bus-side `fadeOutMusic(HANDOFF_FADE_MS)` call entirely (the outro's own
 * source-side envelope/timer owns the theme's dissolve — "the run start path
 * does not cancel/hard-cut the outgoing theme") but keeps the SAME wait
 * before `runTrack` blooms — numerically identical to `HANDOFF_FADE_MS`
 * today, named separately because the two now mean different things (one
 * gates a bus fade that no longer runs on this path; the other is purely
 * "how far into the outro's dissolve does run start overlapping it").
 * Net: run enters ~1.15 s before the theme's own outro timer hard-stops it —
 * the deliberate, bounded ("no *unmanaged* double-playback") crossfade the
 * owner confirmed (P-7).
 */
const HANDOFF_OVERLAP_WAIT_MS = 450;

/** Distinct interlude sources so nested overlays (e.g. pause *during* draft)
 *  compose correctly — the filter holds while any source is active, and the
 *  duck composes independently on top. */
export type InterludeSource = "draft" | "swap" | "pause";

/**
 * AC-1 (2026-ingame-music-rework) — scene→source ROLE routing table: which
 * named track (`preGameTrack` = the theme; `runTrack` = the new procedural
 * `"run"` track, Task-001) each scene plays — resolved via `trackForScene`.
 * `silent`/`boot` never actually request a fresh play (every path into them
 * is a fade-out — `enterBoot`/`onGameOver`/`onReturnToMenu` never call
 * `playMusicFadeIn`), so they route to "preGame" here only for Record
 * completeness, never exercised at runtime. One table — edge-by-edge
 * witnessed in `tests/systems/MusicDirector.test.ts` (spec AC-1); a one-line
 * edit here is the whole mechanism for a future routing change (e.g.
 * Assumption B-2's "victory keeps the theme" alternative).
 */
type MusicSourceRole = "preGame" | "run";
const SOURCE_BY_SCENE: Readonly<Record<MusicScene, MusicSourceRole>> = {
  silent: "preGame",
  boot: "preGame",
  // Task-001 (2026-audio-polish, AC-3) — the theme now starts at the title
  // reveal, not at menu; "title" routes to the SAME preGameTrack.
  title: "preGame",
  menu: "preGame",
  deploy: "preGame",
  combat: "run",
  boss: "run",
  victory: "run",
  defeat: "run",
};

/**
 * MusicDirector — adaptive scene→source music (BL-68 / D-203, split by
 * Task-002 / 2026-ingame-music-rework AC-1).
 *
 * TWO named sources, each continuous WITHIN its own side: `preGameTrack`
 * (the theme) runs from enterMenu() through deploy; `runTrack` (the new
 * procedural `"run"` track) runs continuously from combat through
 * boss → victory/defeat — never restarting mid-run (D-203's "no mid-scene
 * restarts" QUALITY holds; only the *content* decision — one loop forever —
 * is superseded, per the spec's honesty rail). Filter/width/rate/energy
 * transitions are high-band bring-in/take-out + rate/energy sweeps on the
 * running source, chain-side (source-independent). The deploy→combat edge
 * additionally SWAPS the source, hidden inside the existing D-240 riser
 * cellar (`enterCombatSweep`, AC-2). Hard stop only on: enterBoot(),
 * game.over (after defeat fade), returnToMenu, restart (then immediately
 * re-enters combat on the run track).
 *
 * `setIntensity(0..1)` is driven by wave progression / boss presence and
 * modulates brightness + rate + energy within the current scene preset.
 */
export class MusicDirector {
  private engine: MusicEngine;
  private bus: EventBus;
  /** AC-1 (2026-ingame-music-rework) — the two named sources scene routing
   *  resolves between (see `SOURCE_BY_SCENE`/`trackForScene`). */
  private preGameTrack: string;
  private runTrack: string;
  private scene_: MusicScene = "silent";
  private intensity_ = 0;
  /** S195/BL-283 — near-loss pressure overlay (0 = safe … 1 = last life). */
  private pressure_ = 0;
  /** Task-011 (2026-gameplay-reworks, AC-14/O-6, D-325) — the mass-spawn
   *  Stage-C surge overlay (0 = no burst … 1 = max authored surge), driven by
   *  AudioEngine's `audio.spawnSurge` event. Composable exactly like
   *  `pressure_` above. */
  private spawnSurge_ = 0;
  private unsubscribers: (() => void)[] = [];
  // PT-37: interlude overlay state. The filter/width dim holds while ANY source
  // is active; the duck composes independently (only pause ducks).
  private interludeSources_ = new Set<InterludeSource>();
  private interludeDucked_ = false;
  // Task-004 (2026-audio-module-audit, AC-5 leak/re-entrancy sweep) — the
  // enterCombatSweep() settle→lift setTimeout (below), tracked so destroy()
  // can cancel it instead of leaking a live timer handle.
  private sweepTimer_: ReturnType<typeof setTimeout> | null = null;

  constructor(
    engine: MusicEngine,
    bus: EventBus,
    preGameTrack = "theme",
    runTrack = "run",
  ) {
    this.engine = engine;
    this.bus = bus;
    this.preGameTrack = preGameTrack;
    this.runTrack = runTrack;
    this.subscribe();
  }

  /** AC-1 — resolve a scene to its routed source name via
   *  {@link SOURCE_BY_SCENE}. */
  private trackForScene(scene: MusicScene): string {
    return SOURCE_BY_SCENE[scene] === "run" ? this.runTrack : this.preGameTrack;
  }

  get scene(): MusicScene {
    return this.scene_;
  }

  get intensity(): number {
    return this.intensity_;
  }

  /** True while any interlude overlay (draft / swap / pause) is active. */
  get interludeActive(): boolean {
    return this.interludeSources_.size > 0;
  }

  /**
   * Task-001 (2026-audio-polish, AC-3) — the `BootSequence` phase-3 hook:
   * the theme rises DJ-style at the title reveal (LPF+phaser intro,
   * engine-side `playThemeIntro`) instead of the retired `title_loop` SFX,
   * then runs nonstop title → menu → deploy — `enterMenu()`'s existing
   * no-restart guard (below) composes with this for free once the scene is
   * no longer `silent`. Idempotent (scene guard): `BootSequence`'s language
   * re-render calls `startPhase3()` again, which must not re-trigger the
   * intro.
   */
  enterTitle(): void {
    if (this.scene_ === "title") return;
    this.engine.resumeContext?.();
    if (this.scene_ === "silent" || !this.engine.isMusicPlaying?.()) {
      const track = this.trackForScene("title");
      if (this.engine.playThemeIntro) {
        this.engine.playThemeIntro(track, PRESETS.title.fadeMs);
      } else {
        this.engine.playMusicFadeIn(track, PRESETS.title.fadeMs);
      }
    }
    this.applyPreset("title");
    this.scene_ = "title";
  }

  /** Start screen mounts → raise the menu theme. Idempotent. */
  enterMenu(): void {
    if (this.scene_ === "menu") return;
    this.engine.resumeContext?.();
    // Coming from `silent` (incl. right after a returnToMenu fade-out whose
    // deferred source-stop is still pending — isMusicPlaying() reads true then)
    // → always (re)start. playMusicFadeIn cancels the pending fade-out and
    // starts a fresh source, so the menu can never be left permanently silent.
    // Task-001 (AC-3) — coming from `title` (the normal path today) the
    // theme is ALREADY live, so this composes into a no-restart for free.
    if (this.scene_ === "silent" || !this.engine.isMusicPlaying?.()) {
      this.engine.playMusicFadeIn(this.trackForScene("menu"), PRESETS.menu.fadeMs);
    }
    this.applyPreset("menu");
    this.scene_ = "menu";
  }

  /** BACK pressed → boot screen (silent). Fades the loop out. Task-001
   *  (2026-audio-polish, AC-3, P-5) — the next title reveal re-raises the
   *  theme fresh (`enterTitle()`'s own fresh-start FX reset), so BACK→boot→
   *  title always replays a clean intro. */
  enterBoot(): void {
    if (this.scene_ === "silent") return;
    this.engine.fadeOutMusic(600);
    this.scene_ = "silent";
  }

  /** DEPLOY pressed → muffle the loop (continuous, no stop) AND start the
   *  theme's own source-side outro (Task-001, 2026-audio-polish, AC-5) — the
   *  2.4 s dissolve `enterCombatSweep()` later detects to pick the crossfade
   *  hand-off instead of the old bus-fade one. */
  onDeploy(): void {
    if (this.scene_ === "silent") return;
    this.applyPreset("deploy");
    this.engine.startThemeOutro?.();
    this.scene_ = "deploy";
  }

  /** Run started → the "drop": open the filter on the run track.
   *
   * AC-1 (2026-ingame-music-rework) — unlike `enterMenu()` (which only
   * restarts when NOTHING is audible, since menu/deploy share one
   * continuous pre-game track), `enterCombat()` always starts `runTrack`
   * when arriving from a non-combat scene: every reachable predecessor
   * (silent/menu/deploy) rides the THEME, a different named source, which
   * must never be audible during a run — there is no "already playing, so
   * skip the restart" case left for a source that, by construction, cannot
   * yet be live here. (No production caller today — `enterCombatSweep()` is
   * the deploy→run seam; this stays the "plain, no sweep" variant for
   * restart/programmatic flows, tech-analysis §1.2.) */
  enterCombat(): void {
    if (this.scene_ === "combat") return;
    this.engine.playMusicFadeIn(this.trackForScene("combat"), PRESETS.combat.fadeMs);
    this.applyPreset("combat");
    this.intensity_ = 0;
    this.scene_ = "combat";
  }

  /**
   * S143 / BL-129(b): the prescreen→game french-house riser. Same end state as
   * `enterCombat()` (combat scene, intensity 0, bright/wide tone) but the
   * brightness + width *sweep* up smoothly from a deeper dampened start over
   * SWEEP_DURATION_MS, rather than jumping over the combat preset's 600 ms
   * lift. The sweep is one-shot: callers should pick THIS for the deploy→game
   * seam and the plain `enterCombat()` elsewhere (restart, programmatic flow).
   *
   * AC-2 (2026-ingame-music-rework) — the D-240 riser is now ALSO the
   * theme→run hand-off (tech-analysis §1.3): the dampen still snaps
   * immediately (chain-side, so it darkens whatever is CURRENTLY live —
   * the dying theme), but instead of the settle→lift firing off the bare
   * SWEEP_SETTLE_MS, the theme first DISSOLVES via `fadeOutMusic`
   * (HANDOFF_FADE_MS — its native branch stops the iOS native theme too,
   * one call, cross-platform) and `runTrack` blooms out of the cellar only
   * once that hand-off timer fires — cancel-safe via the SAME tracked
   * `sweepTimer_` (AA-19 discipline, below). A cold start (nothing was
   * playing — e.g. a defensive/direct entry) has no theme to dissolve, so
   * it keeps the ORIGINAL bare-settle shape, just on `runTrack`.
   *
   * Mechanism: cancel any pending ramp, snap to a deeper dampen, hold for a
   * short settle (so the sweep reads as a *deliberate* riser instead of a tail
   * of the deploy ramp), then linearly ramp brightness/width up to the combat
   * targets across SWEEP_DURATION_MS. Width carries the "bloom" the way PT-37
   * already does in normal combat — we're reusing the primitive, not adding a
   * new node (D-215 honoured; D-203's *quality* — no mid-scene restarts —
   * holds within each of the two sources, per the class doc).
   *
   * Task-001 (2026-audio-polish, AC-5) — a THIRD variant, the CROSSFADE
   * hand-off: `onDeploy()` now also starts the theme's own 2.4 s source-side
   * outro. When that outro is still in flight here (the normal deploy path —
   * `enterCombatSweep()` fires ~800 ms after `onDeploy()`, well inside the
   * outro's window), the OLD bus-side `fadeOutMusic(HANDOFF_FADE_MS)` call is
   * skipped entirely (the outro's own tracked timer is the theme's single
   * owner of its stop — AA-19; this call must not cancel/hard-cut it) but
   * `runTrack` still blooms at the SAME wait (`HANDOFF_OVERLAP_WAIT_MS`) —
   * the theme's dissolving tail and the run's dark bloom deliberately
   * overlap (owner-confirmed crossfade, P-7). A bare engine (no
   * `isThemeOutroInFlight`) or one where `onDeploy()` never actually started
   * an outro (nothing was playing to dissolve) transparently falls back to
   * the ORIGINAL bus-fade hand-off shape below — unchanged.
   */
  enterCombatSweep(): void {
    if (this.scene_ === "combat") return;
    // Tidy (non-blocking note from the Task-004 audit ledger): cancel any
    // still-pending sweep timer from a prior re-entrant call (deploy → sweep
    // → interrupt → deploy → sweep again, all inside the settle/hand-off
    // window) before scheduling a new one. Without this, the stale timer's
    // callback still fires and unconditionally nulls `sweepTimer_`, silently
    // dropping destroy()'s handle to the NEWER pending timer instead of its
    // own — benign today (both timers ramp toward the same combat target,
    // see the lift below), but a latent leak of the tracked handle.
    if (this.sweepTimer_) {
      clearTimeout(this.sweepTimer_);
      this.sweepTimer_ = null;
    }
    const combat = PRESETS.combat;
    // 1. Dip below the deploy preset (the anticipation cellar) — chain-side,
    //    so this darkens whatever is CURRENTLY live exactly as it darkens a
    //    running loop in the cold-start branch below.
    this.engine.setMusicBrightness?.(SWEEP_DAMPEN_BRIGHTNESS, SWEEP_SETTLE_MS);
    this.engine.setMusicWidth?.(SWEEP_DAMPEN_WIDTH, SWEEP_SETTLE_MS);
    this.engine.setMusicRate?.(combat.rate, SWEEP_SETTLE_MS);

    const coldStart = this.scene_ === "silent" || !this.engine.isMusicPlaying?.();
    // AC-5 — only meaningful when there is actually a live theme to dissolve.
    const outroInFlight =
      !coldStart && (this.engine.isThemeOutroInFlight?.() ?? false);
    if (coldStart) {
      // Nothing to dissolve — start runTrack immediately, same shape as
      // enterCombat()'s own cold-start case, just on the run track.
      this.engine.playMusicFadeIn(this.trackForScene("combat"), combat.fadeMs);
    } else if (outroInFlight) {
      // AC-5 crossfade: the outro already owns the dissolve — skip the bus
      // fade, keep the trace (still names both tracks + the hand-off shape).
      this.engine.traceMusicEvent?.(
        "music.handoff",
        `${this.preGameTrack}->${this.runTrack} crossfade ${HANDOFF_OVERLAP_WAIT_MS}ms`,
      );
    } else {
      // AC-2 hand-off (unchanged): dissolve the theme DOWN INTO the cellar we
      // just dampened into; `runTrack` blooms out of it once it's gone (below).
      this.engine.traceMusicEvent?.(
        "music.handoff",
        `${this.preGameTrack}->${this.runTrack} in ${HANDOFF_FADE_MS}ms`,
      );
      this.engine.fadeOutMusic(HANDOFF_FADE_MS);
    }

    // 2. Schedule the sweep up — the linear ramp from the dampen → combat is
    //    what reads as the french-house "filter opening" feel. Cold start:
    //    fires after the bare settle (unchanged shape). Hand-off/crossfade:
    //    fires HANDOFF_FADE_MS/HANDOFF_OVERLAP_WAIT_MS later (numerically the
    //    same 450 ms today), at which point `runTrack` is started INSIDE the
    //    already-dampened cellar tone before the SAME lift opens it.
    //
    // Task-004 (2026-audio-module-audit, AC-5 leak/re-entrancy sweep) — this
    // timer used to be fire-and-forget: a scene change WITHIN the settle/
    // hand-off window (BACK to boot, game-over, boss-incoming, restart —
    // none of which can re-enter this method, since the guard above only
    // blocks a second sweep) left it live, and it would fire later and ramp
    // brightness/width TOWARD COMBAT (and, now, start runTrack) on top of
    // whatever the new scene had just set — audibly wrong (e.g. bleeding
    // tone back up during a fade-to-silence, or starting run music after a
    // BACK/game-over cancelled the hand-off). Tracked in `sweepTimer_`
    // (cancelled in destroy()) and guarded on fire: only apply if we're
    // STILL in the scene this sweep started for — a cancelled hand-off
    // therefore starts nothing and leaves no orphaned timer (AA-19).
    this.sweepTimer_ = setTimeout(
      () => {
        this.sweepTimer_ = null;
        if (this.scene_ !== "combat") return;
        if (!coldStart) {
          this.engine.playMusicFadeIn(
            this.trackForScene("combat"),
            HANDOFF_BLOOM_FADE_MS,
          );
        }
        this.engine.setMusicBrightness?.(combat.brightness, SWEEP_DURATION_MS);
        this.engine.setMusicWidth?.(combat.width, SWEEP_DURATION_MS);
      },
      coldStart
        ? SWEEP_SETTLE_MS
        : outroInFlight
          ? HANDOFF_OVERLAP_WAIT_MS
          : HANDOFF_FADE_MS,
    );
    this.intensity_ = 0;
    this.scene_ = "combat";
  }

  /** Set intensity (0..1), driven by wave progression / density / boss. */
  setIntensity(value: number): void {
    this.intensity_ = Math.max(0, Math.min(1, value));
    if (this.scene_ !== "combat" && this.scene_ !== "boss") return;
    // PT-37: while an interlude overlays the loop, STORE the intensity but skip
    // the brightness/width writes so the dim is not stomped. It is re-applied
    // (via effectiveSceneTone) when the last interlude source releases.
    if (this.interludeActive) return;
    const { brightness, rate, energy } = this.effectiveSceneTone();
    this.engine.setMusicBrightness?.(brightness, 200);
    this.engine.setMusicRate?.(rate, 200);
    this.engine.setMusicEnergy?.(energy, 200);
  }

  /**
   * S195/BL-283 — the near-loss PRESSURE layer (audit beat #9: the director
   * was lives-blind). A composable overlay in the interlude's manner: the
   * pressure folds into {@link effectiveSceneTone} (never touches `scene_`),
   * so every existing restore path re-applies it for free. Driven by the
   * render-side NearLossDirector (0 = safe … 1 = last life); band 0 releases
   * cleanly — the comeback lands on a calm mix.
   */
  setPressure(value: number): void {
    const p = Math.max(0, Math.min(1, value));
    if (p === this.pressure_) return;
    this.pressure_ = p;
    if (this.scene_ !== "combat" && this.scene_ !== "boss") return;
    if (this.interludeActive) return; // stored; re-applied on release
    const tone = this.effectiveSceneTone();
    this.engine.setMusicBrightness?.(tone.brightness, PRESSURE_RAMP_MS);
    this.engine.setMusicWidth?.(tone.width, PRESSURE_RAMP_MS);
    this.engine.setMusicRate?.(tone.rate, PRESSURE_RAMP_MS);
    this.engine.setMusicEnergy?.(tone.energy, PRESSURE_RAMP_MS);
  }

  /**
   * Task-011 (2026-gameplay-reworks, AC-14/O-6, D-325) — Stage C's density-
   * to-intensity move. A composable overlay in EXACTLY `setPressure`'s
   * manner (never touches `scene_`, folds into {@link effectiveSceneTone},
   * every existing restore path re-applies it for free). AudioEngine is the
   * ONLY caller (via the `audio.spawnSurge` bus event this class subscribes
   * to below) — this keeps the density→energy model single-owner here,
   * rather than AudioEngine racing MusicDirector's own periodic
   * `setMusicEnergy` writes (the paper's own risk note, §1.3 Option C).
   * Energy-only (no brightness/width/rate change): the surge is meant to
   * read as the composer's tension layer swelling, not a tonal shift.
   */
  setSpawnSurge(value: number): void {
    const s = Math.max(0, Math.min(1, value));
    if (s === this.spawnSurge_) return;
    this.spawnSurge_ = s;
    if (this.scene_ !== "combat" && this.scene_ !== "boss") return;
    if (this.interludeActive) return; // stored; re-applied on release
    const tone = this.effectiveSceneTone();
    this.engine.setMusicEnergy?.(tone.energy, SPAWN_SURGE_RAMP_MS);
  }

  /** Current spawn-surge intensity (0..1) — exposed for tests. */
  get spawnSurge(): number {
    return this.spawnSurge_;
  }

  /** Current near-loss pressure (0..1) — exposed for tests. */
  get pressure(): number {
    return this.pressure_;
  }

  /**
   * Interlude overlay (PT-37): dumpf + narrow (+ optional duck) on the running
   * loop, WITHOUT touching `scene_`. Composes across sources (draft/swap/pause)
   * — the filter holds while any source is active; the pause-duck adds/releases
   * independently. Idempotent per source.
   *
   * Task-003 (2026-audio-polish, AC-10) — every source (draft/swap/pause, not
   * just pause) ALSO drives the underwater muffle (`setMusicMuffle`), on the
   * SAME "first source in / last source out" schedule as the brightness/width
   * dim (idempotent, no re-ramp on a nested second source) — composing with
   * the pause-only duck on top when both are present. Uses the SAME
   * `INTERLUDE_IN_MS`/`INTERLUDE_OUT_MS` ramp times as the dim (no new timing
   * invented) and reaches full neutral release in the same place the dim
   * does, so a fully-released interlude is provably neutral on both.
   */
  setInterlude(
    active: boolean,
    opts?: { duck?: boolean; source?: InterludeSource },
  ): void {
    const source: InterludeSource = opts?.source ?? "draft";
    const wasActive = this.interludeActive;

    if (active) {
      this.interludeSources_.add(source);
      // Ramp the filter/width/muffle in only on the FIRST source (idempotent
      // after).
      if (!wasActive) {
        this.engine.setMusicBrightness?.(INTERLUDE_BRIGHTNESS, INTERLUDE_IN_MS);
        this.engine.setMusicWidth?.(INTERLUDE_WIDTH, INTERLUDE_IN_MS);
        this.engine.setMusicMuffle?.(true, INTERLUDE_IN_MS);
      }
      // The duck composes on top of any existing dim+muffle (pause during draft).
      if (opts?.duck && !this.interludeDucked_) {
        this.interludeDucked_ = true;
        this.engine.setMusicDuck?.(INTERLUDE_DUCK_DB, INTERLUDE_IN_MS);
      }
      return;
    }

    if (!this.interludeSources_.has(source)) return; // unbalanced release → ignore
    this.interludeSources_.delete(source);
    // Release the duck when its (pause) source lifts, even if a draft/swap
    // dim+muffle remains.
    if (opts?.duck && this.interludeDucked_) {
      this.interludeDucked_ = false;
      this.engine.setMusicDuck?.(0, INTERLUDE_OUT_MS);
    }
    // Restore the scene's effective tone (+ release the muffle to neutral)
    // only when the LAST source releases.
    if (this.interludeSources_.size === 0) {
      const tone = this.effectiveSceneTone();
      this.engine.setMusicBrightness?.(tone.brightness, INTERLUDE_OUT_MS);
      this.engine.setMusicWidth?.(tone.width, INTERLUDE_OUT_MS);
      this.engine.setMusicMuffle?.(false, INTERLUDE_OUT_MS);
      this.engine.setMusicRate?.(tone.rate, INTERLUDE_OUT_MS);
      // AC-4 (2026-ingame-music-rework) — energy rides the SAME store-and-skip
      // pattern "for free": any setIntensity/setPressure call made WHILE this
      // interlude was active already skipped its energy write (the guards
      // above), so the STORED intensity_/pressure_ only reaches the composer
      // here, on release — never stomped mid-interlude.
      this.engine.setMusicEnergy?.(tone.energy, INTERLUDE_OUT_MS);
      // Safety net: never leave a duck hanging once everything is released.
      if (this.interludeDucked_) {
        this.interludeDucked_ = false;
        this.engine.setMusicDuck?.(0, INTERLUDE_OUT_MS);
      }
    }
  }

  /**
   * The current scene's effective brightness/width/rate/energy — the
   * combat/boss variants fold in `intensity_` (matching `setIntensity`'s
   * math), others use the bare preset. Used to ramp back out of an
   * interlude (and by `setIntensity`/`setPressure` directly).
   *
   * AC-4 (2026-ingame-music-rework) — `energy` gets the SAME intensity fold
   * as brightness (wave progression densifies the composer's layers) PLUS a
   * pressure boost: near-loss pressure must reach the composer's L4 tension
   * layer (`LAYER_ENERGY.tension.threshold = 0.75`, `MusicPatterns.ts`) —
   * "pressure becomes musical", not just tonal (tech-analysis §2.5). At
   * combat's 0.45 base + full intensity (+0.4·0.55≈0.22) + full pressure
   * (+0.35), a last-life spike comfortably crosses the tension threshold
   * even early in a run; boss's 0.9 base is already past it unconditionally.
   */
  private effectiveSceneTone(): {
    brightness: number;
    width: number;
    rate: number;
    energy: number;
  } {
    const base = PRESETS[this.scene_];
    if (this.scene_ === "combat" || this.scene_ === "boss") {
      // S195/BL-283 — the near-loss pressure darkens + narrows + urges the
      // combat tone (brightness −0.12·p, width −0.15·p, rate +0.02·p), capped
      // by construction; released at p=0 with no residue.
      const p = this.pressure_;
      // Task-011 (AC-14/O-6, D-325) — the spawn-surge is energy-ONLY (no
      // brightness/width/rate change, unlike pressure): a mass-spawn burst
      // should swell the tension layer, not re-tint the whole mix.
      const s = this.spawnSurge_;
      return {
        brightness: Math.max(
          0,
          base.brightness +
            this.intensity_ * (1 - base.brightness) * 0.4 -
            0.12 * p,
        ),
        width: Math.max(0, base.width - 0.15 * p),
        rate: base.rate + this.intensity_ * 0.02 + 0.02 * p,
        energy: Math.min(
          1,
          base.energy +
            this.intensity_ * (1 - base.energy) * 0.4 +
            0.35 * p +
            0.3 * s,
        ),
      };
    }
    return {
      brightness: base.brightness,
      width: base.width,
      rate: base.rate,
      energy: base.energy,
    };
  }

  private subscribe(): void {
    this.unsubscribers.push(
      this.bus.on("game.over", () => this.onGameOver()),
      this.bus.on("game.returnToMenu", () => this.onReturnToMenu()),
      this.bus.on("game.restart", () => this.onRestart()),
      this.bus.on("game.won", () => this.onVictory()),
      this.bus.on("boss.wave.incoming", () => this.onBossIncoming()),
      // Task-011 (2026-gameplay-reworks, AC-14/O-6, D-325) — the ONLY
      // subscriber of AudioEngine's spawn-density report.
      this.bus.on("audio.spawnSurge", (d: unknown) => {
        const ev = d as { intensity?: number } | undefined;
        this.setSpawnSurge(ev?.intensity ?? 0);
      }),
      this.bus.on("boss.defeated", (d: unknown) => {
        // BL-160 (S160 audit): pre-BL-163, boss.defeated fired for every isBoss()
        // death (maxHp ≥ 500), so a scaled warm-up enemy dying mid boss-wave would
        // prematurely drop the boss music to combat. Guard on the real bosses,
        // mirroring the AudioEngine/boss.spawned guard. BL-163 (S163) retired the
        // maxHp heuristic at the source (the emit is now real-boss-only), so this
        // guard is now belt-and-suspenders. Music-only.
        const t = (d as { boss?: { special?: { type?: string } } } | undefined)
          ?.boss?.special?.type;
        if (t === "finalBoss" || t === "spawnAdds") this.onBossDefeated();
      }),
      this.bus.on("wave.started", (d: unknown) => {
        const ev = d as { wave?: number } | undefined;
        const wave = ev?.wave ?? 1;
        this.setIntensity(Math.min(1, wave / 35));
      }),
      // PT-37: interlude overlays. The director only ever READS these signals
      // (sim-blind) — the loop keeps running, never restarts.
      this.bus.on("draft.cardsDrawn", () =>
        this.setInterlude(true, { source: "draft" }),
      ),
      this.bus.on("draft.closed", () =>
        this.setInterlude(false, { source: "draft" }),
      ),
      this.bus.on("skill.swap.shown", () =>
        this.setInterlude(true, { source: "swap" }),
      ),
      this.bus.on("skill.swap.hidden", () =>
        this.setInterlude(false, { source: "swap" }),
      ),
      // Pause additionally ducks; both game.paused/game.resumed already exist
      // (balanced toggle from HUD/ESC) — the director just reads them.
      this.bus.on("game.paused", () =>
        this.setInterlude(true, { duck: true, source: "pause" }),
      ),
      this.bus.on("game.resumed", () =>
        this.setInterlude(false, { duck: true, source: "pause" }),
      ),
    );
  }

  private onBossIncoming(): void {
    if (this.scene_ !== "combat") return;
    this.applyPreset("boss");
    this.scene_ = "boss";
  }

  private onBossDefeated(): void {
    if (this.scene_ !== "boss") return;
    this.applyPreset("combat");
    this.scene_ = "combat";
  }

  private onVictory(): void {
    this.pressure_ = 0; // the run is over — never carry pressure across
    this.spawnSurge_ = 0; // Task-011 — nor the spawn-surge
    if (this.scene_ === "silent") return;
    this.applyPreset("victory");
    this.scene_ = "victory";
  }

  /** Defeat: apply pitch-down + muffle preset, then fade to silence. */
  private onGameOver(): void {
    this.pressure_ = 0;
    this.spawnSurge_ = 0; // Task-011 — nor the spawn-surge
    if (this.scene_ === "silent") return;
    this.applyPreset("defeat");
    this.engine.fadeOutMusic(PRESETS.defeat.fadeMs);
    this.scene_ = "silent";
  }

  private onReturnToMenu(): void {
    this.pressure_ = 0;
    this.spawnSurge_ = 0; // Task-011 — nor the spawn-surge
    if (this.scene_ === "silent") return;
    this.engine.fadeOutMusic(800);
    this.scene_ = "silent";
  }

  /** Restart: fade out, then fresh run-track loop in combat — plain, no
   *  sweep (tech-analysis §1.2's edge table: "matches today's 'restart keeps
   *  plain enterCombat'"). The subsequent `enterCombat()` from the restart
   *  flow is then a no-op. */
  private onRestart(): void {
    this.pressure_ = 0;
    this.spawnSurge_ = 0; // Task-011 — nor the spawn-surge
    this.engine.fadeOutMusic(300);
    this.engine.playMusicFadeIn(this.trackForScene("combat"), PRESETS.combat.fadeMs);
    this.applyPreset("combat");
    this.intensity_ = 0;
    this.scene_ = "combat";
  }

  private applyPreset(scene: MusicScene): void {
    const p = PRESETS[scene];
    this.engine.setMusicBrightness?.(p.brightness, p.fadeMs);
    this.engine.setMusicWidth?.(p.width, p.fadeMs);
    this.engine.setMusicRate?.(p.rate, p.fadeMs);
    this.engine.setMusicEnergy?.(p.energy, p.fadeMs);
  }

  destroy(): void {
    if (this.sweepTimer_) {
      clearTimeout(this.sweepTimer_);
      this.sweepTimer_ = null;
    }
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
  }
}
